import React, { useEffect, useState } from "react";
import styles from "./CartItemVariantsInfo.module.css"; // CSS Module
import { fetchVariantsByIds } from "../../apps/Bundles/constants";
import { ChevronDownIcon, ChevronUpIcon } from "@shopify/polaris-icons";
import { isMeinSchoenerStore } from "../../apps/constants";

interface CartItemVariantsInfoProps {
    variantIds: string[];
    standalone?: boolean;
    quantity?: number;
    storefrontAccessToken: string;
    variantQuantities?: Record<string, number>;
    defaultOpen?: boolean;
}

interface VariantData {
    id: string;
    title: string;

    selectedOptions: { name: string; value: string }[];
    image?: { url: string };
    product: {
        title: string;
        handle: string;
    };
}

const CartItemVariantsInfo: React.FC<CartItemVariantsInfoProps> = ({
    variantIds,
    standalone = true,
    storefrontAccessToken,
    quantity = 1,
    variantQuantities,
    defaultOpen = false,
}) => {
    const [open, setOpen] = useState(defaultOpen);
    const [variantsData, setVariantsData] = useState<VariantData[] | null>(null);
    const [loading, setLoading] = useState(false);

    const loadVariants = async () => {
        if (variantsData || loading) return;
        setLoading(true);

        // Convert numeric IDs to global IDs
        const globalIds = variantIds.map((id) =>
            id?.toString()?.includes("gid://shopify/") ? id : `gid://shopify/ProductVariant/${id}`,
        );
        const data = await fetchVariantsByIds(globalIds, standalone, storefrontAccessToken);

        setVariantsData(data);
        setLoading(false);
    };

    useEffect(() => {
        if (defaultOpen) loadVariants();
    }, []);

    const toggleOpen = async () => {
        setOpen(!open);
        await loadVariants();
    };

    if (!variantIds || variantIds.length === 0) {
        return <div className={styles.container}>No variants found.</div>;
    }

    const totalSubItems = variantQuantities
        ? Object.values(variantQuantities).reduce((a, b) => a + b, 0) * quantity
        : variantIds.length * quantity;

    return (
        <div className={styles.container}>
            {/* Header with count and chevron */}
            <div className={styles.header} onClick={toggleOpen}>
                {isMeinSchoenerStore ? (
                    <>{totalSubItems} Produkte </>
                ) : (
                    <>
                        {totalSubItems} item{totalSubItems > 1 ? "s" : ""}{" "}
                    </>
                )}
                <span className={styles.chevron}>
                    {open ? (
                        <ChevronUpIcon width={"16px"} height={"16px"} />
                    ) : (
                        <ChevronDownIcon width={"16px"} height={"16px"} />
                    )}
                </span>
            </div>

            {/* Variant details */}
            {open && (
                <div className={styles.details}>
                    {loading && (
                        <div className={styles.loading}>
                            {isMeinSchoenerStore ? "Varianten werden geladen…" : "Loading variants…"}
                        </div>
                    )}

                    {!loading && variantsData && (
                        <ul className={styles.variantList}>
                            {variantsData.map((variant) => {
                                if (!variant) return null;

                                const numericId = variant.id.split("/").pop();
                                const itemQty =
                                    (variantQuantities && numericId ? variantQuantities[numericId] : 1) * quantity;

                                return (
                                    <li key={variant.id} className={styles.variantItem}>
                                        {variant.image && (
                                            <img
                                                src={variant.image.url}
                                                alt={variant.title}
                                                className={styles.variantImage}
                                            />
                                        )}
                                        <div className={styles.variantText}>
                                            <div className={styles.productTitle}>
                                                {itemQty} x {variant.product.title}
                                            </div>
                                            {variant.selectedOptions
                                                .map((opt) => opt.value)
                                                .filter((val) => val !== "Default Title")
                                                .join(" / ") && (
                                                <div className={styles.variantOptions}>
                                                    {variant.selectedOptions
                                                        .map((opt) => opt.value)
                                                        .filter((val) => val !== "Default Title")
                                                        .join(" / ")}
                                                </div>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
};

export default CartItemVariantsInfo;
